import { useState } from 'react';

/**
 * Header for public/shared pages that displays the project logo + name
 * registered by the super admin. When there's no logo registered, shows a
 * neutral colored placeholder with the project's initial letter (no Exseg
 * fallback — per user request).
 *
 * Props:
 *  - projectId: string | undefined
 *  - projectName: string | undefined
 *  - projectLogoUrl: string | undefined  ("/api/projects/{id}/logo")
 *  - subtitle: string | ReactNode | undefined
 *  - rightSlot: ReactNode | undefined
 *  - dark: boolean
 */
export default function ProjectLogoHeader({
  projectId,
  projectName,
  projectLogoUrl,
  subtitle,
  rightSlot,
  dark = false,
}) {
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = projectLogoUrl && !logoFailed;
  const initial = (projectName || '?').trim().charAt(0).toUpperCase();

  const baseClass = dark
    ? 'bg-gray-900/90 border-b border-gray-800 text-white'
    : 'bg-white/95 border-b border-gray-200 text-gray-900';

  return (
    <header
      className={`sticky top-0 z-20 backdrop-blur-md ${baseClass}`}
      data-testid="project-logo-header"
    >
      <div className="container mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {showLogo ? (
            <img
              src={projectLogoUrl}
              alt={projectName || 'Logo do projeto'}
              className={`h-12 w-12 object-contain rounded-lg flex-shrink-0 p-1 ${dark ? 'bg-white/95 border border-white/10 shadow-md' : 'bg-white border border-gray-200 shadow-sm'}`}
              onError={() => setLogoFailed(true)}
              data-testid="project-logo-img"
            />
          ) : (
            <div
              className={`h-12 w-12 flex items-center justify-center rounded-lg flex-shrink-0 font-bold text-xl tracking-tight ${dark ? 'bg-gold/20 text-gold border border-gold/30' : 'bg-gold/15 text-gold border border-gold/30'}`}
              data-testid="project-logo-placeholder"
            >
              {initial}
            </div>
          )}
          <div className="min-w-0">
            <div
              className={`text-[10px] uppercase tracking-[0.2em] font-bold text-gold`}
              data-testid="project-logo-tagline"
            >
              GESTÃO DE RPAS
            </div>
            <h1
              className={`text-base sm:text-lg font-semibold leading-tight truncate ${dark ? 'text-white' : 'text-gray-900'}`}
              data-testid="project-logo-name"
            >
              {projectName || 'Projeto'}
            </h1>
            {subtitle && (
              <div className={`text-[11px] mt-0.5 truncate ${dark ? 'text-gray-300' : 'text-gray-500'}`}>
                {subtitle}
              </div>
            )}
          </div>
        </div>
        {rightSlot && <div className="flex-shrink-0 flex items-center gap-3">{rightSlot}</div>}
      </div>
    </header>
  );
}
